import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { get } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface Props {
  control: any;
  errors: any;
  connected: boolean;
  isBillingAddressHidden: boolean;
  selectedAccountId?: string;
  setValue: any;
}

const AccountGeneralInfo = ({
  control,
  errors,
  connected,
  isBillingAddressHidden,
  selectedAccountId,
  setValue,
}: Props) => {
  const { userStore } = useStores();
  const hasChildCompanies = userStore.userCompanies.length > 1;
  const { getDepartmentByCompanyIdTypeahead } = useDepartment();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [showAddressDetails, setShowAddressDetails] = useState(false);

  const selectedAddress = control._formValues?.address as Nullable<AddressItem>;
  const addressProvider = SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH;
  const { getPlaces, getAddressItemByGoogleMapsPlaceId } = useGeocode({
    addressProvider,
  });

  const handleAddressChange = useCallback(
    async (item: AddressItem, fillAddressDetails?: true) => {
      if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
        const addressItem = await getAddressItemByGoogleMapsPlaceId(
          item.placeId,
          item.fullAddress ?? undefined,
        );

        if (fillAddressDetails) {
          setValue('address.premise', addressItem.premise);
          setValue('address.locality', addressItem.locality);
          setValue('address.administrativeArea', addressItem.administrativeArea);
          setValue('address.postalCode', addressItem.postalCode);

          setShowAddressDetails(true);
        }

        return addressItem;
      }

      return item;
    },
    [getAddressItemByGoogleMapsPlaceId, setValue, setShowAddressDetails],
  );

  useEffect(() => {
    if (selectedAddress) {
      const { premise, locality, administrativeArea, postalCode } = selectedAddress;
      const hasAnyValue = premise || locality || administrativeArea || postalCode;

      if (hasAnyValue) {
        setShowAddressDetails(true);
      }
    }
  }, [selectedAddress]);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="form-section-title">
            {t('account.form.account_details')}
          </Typography>
        </Grid>
        {hasChildCompanies && (
          <Grid size={{ xs: 12 }}>
            <CompanySelectorFormField
              sx={{ width: '100%', minWidth: '100%' }}
              name={'company'}
              isRequired={true}
              disabled={!!selectedAccountId}
              errors={errors}
              control={control}
            />
          </Grid>
        )}
        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={errors}
            name="name"
            label={`${t('form_fields.account_name')}`}
            isRequired={true}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={errors}
            name="externalId"
            label={`${t('form_fields.external_id')}`}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <AutocompleteAsyncFormField
            control={control}
            errors={errors}
            name="department"
            asyncCallback={getDepartmentByCompanyIdTypeahead}
            extraRequestOptions={{
              companyId: companyId,
            }}
            getValue={(item) => item.id}
            getLabel={(item) => item.name || ''}
            label={`${t('form_fields.department')}`}
            clearable
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={errors}
            name="accountingId"
            label={`${t('form_fields.accounting_id')}`}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AutocompleteAsyncFormField
            control={control}
            errors={errors}
            name="address"
            getLabel={(item) => get(item, 'nameWithAddress', '')}
            getValue={(item) => get(item, 'placeId', '')}
            label={`${t('form_fields.address')}`}
            placeholder={`${t('common.address_autocomplete_hint')}`}
            asyncCallback={getPlaces}
            onSelect={(item: AddressItem) => handleAddressChange(item, true)}
            isRequired={connected}
            debounceTime={500}
          />

          <Collapse in={showAddressDetails}>
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid size={{ xs: 12 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.premise"
                  label={`${t('form_fields.address_2')}`}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.locality"
                  label={`${t('form_fields.city')}`}
                  isRequired={true}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.administrativeArea"
                  label={`${t('form_fields.state')}`}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.postalCode"
                  label={`${t('form_fields.zip')}`}
                  isRequired={true}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      </Grid>

      {(!connected || selectedAccountId) && (
        <Box>
          <CheckBoxFormField
            control={control}
            errors={errors}
            value={false}
            label={t('account.form.billing_same_as_primary_address') || ''}
            name={'isBillingAddressSame'}
          />
          <Collapse in={!isBillingAddressHidden}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              name="billingAddress"
              getLabel={(item) => get(item, 'nameWithAddress', '')}
              getValue={(item) => get(item, 'placeId', '')}
              label={`${t('form_fields.billing_address')}`}
              placeholder={`${t('common.address_autocomplete_hint')}`}
              asyncCallback={getPlaces}
              onSelect={handleAddressChange}
              sx={{ mt: 2 }}
            />
          </Collapse>
        </Box>
      )}
    </Card>
  );
};

export { AccountGeneralInfo };
